// The show team: producer, designer and pilot. Shows projects get a drawn team
// that an approver signs off; every other channel is picked by hand.

import React, { useState } from 'react';
import { useToast } from '../components/Toast/ToastContext';
import Appear from '../components/ui/Appear';
import Avatar from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import Select from '../components/ui/Select';
import { Tag } from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import Tooltip from '../components/ui/Tooltip';
import { IconCheck } from '../components/ui/icons';
import { approveTeam, drawTeam, setTeamMember } from './sparkApi';
import type { Person, ProductionDetail, TeamMember } from './sparkApi';
import type { TeamRole } from './sparkRules';
import { useBecame } from './SparkProps';
import { errorMessage } from './sparkUi';

const ROLES: { key: TeamRole; label: string }[] = [
  { key: 'producer', label: 'Producer' },
  { key: 'designer', label: 'Designer' },
  { key: 'pilot', label: 'Pilot' },
];

const canTake = (role: TeamRole, p: Person) =>
  role === 'producer' ? p.producer
    : role === 'pilot' ? p.pilot
      : p.roles.includes('designer') || p.roles.includes('director');

const SparkTeamPanel: React.FC<{
  detail: ProductionDetail;
  people: Person[];
  studio: boolean;
  approver: boolean;
  userId: string;
  onChanged: () => void;
}> = ({ detail, people, studio, approver, userId, onChanged }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [ties, setTies] = useState<TeamRole[]>([]);
  const p = detail.production;
  const isShows = p.channel === 'shows';
  const archived = !!p.archived_at;
  const member = (role: TeamRole): TeamMember | undefined => detail.team.find((m) => m.role === role);
  const filled = detail.team.filter((m) => m.user_id);
  const approved = filled.length > 0 && filled.every((m) => m.approved_at);
  const onTeam = filled.some((m) => m.user_id === userId && m.approved_at);
  const canPick = !archived && (approver || onTeam || (studio && !isShows));
  const canDraw = approver && isShows && !archived;
  const justApproved = useBecame(approved);

  const run = async (fn: () => Promise<unknown>, fail: string, ok?: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, fail));
    }
    setBusy(false);
  };

  const draw = (role?: TeamRole) =>
    run(async () => {
      const r = await drawTeam(p.id, role);
      setTies(r.ties || []);
    }, 'Could not draw the team. Try again.');

  return (
    <Panel
      title={isShows ? 'Show team' : 'Team'}
      actions={isShows ? <Appear show={filled.length > 0 && !approved} className="inline-flex"><Tag hex={CATEGORY.orange.ink.hex}>Draft</Tag></Appear> : undefined}
      footer={isShows && !archived ? (
        <div className="flex flex-wrap items-center gap-2">
          {approved ? (
            <span className={`inline-flex items-center gap-1.5 text-sm text-green-300 ${justApproved ? 'motion-safe:animate-rowLand' : ''}`}>
              <IconCheck className="h-3.5 w-3.5" draw={justApproved} />Approved. The team has been told.
            </span>
          ) : filled.length === 0 ? (
            <span className="text-sm text-on-surface-variant">{canDraw ? 'No team yet.' : 'No team yet. An approver draws it.'}</span>
          ) : (
            <span className="text-sm text-on-surface-variant">Nobody has been told yet.</span>
          )}
          <Appear show={canDraw && !approved} className="ml-auto flex flex-wrap gap-2">
            <Tooltip label={filled.length ? 'Draw every role again' : 'Draw a producer, designer and pilot'}>
              <Button size="sm" disabled={busy} onClick={() => draw()}>{filled.length ? 'Redraw all' : 'Draw the team'}</Button>
            </Tooltip>
            <Appear show={filled.length > 0} className="inline-flex">
              <Tooltip label="Approve and tell the team">
                <Button
                  variant="filled"
                  size="sm"
                  disabled={busy}
                  onClick={() => run(() => approveTeam(p.id), 'Could not approve the team. Try again.', 'Team approved and told.')}
                >
                  Approve the team
                </Button>
              </Tooltip>
            </Appear>
          </Appear>
        </div>
      ) : undefined}
    >
      <p className="m-0 -mt-1.5 text-sm text-on-surface-variant">
        {isShows ? 'Who answers for this show from each angle.' : 'People are picked by hand on this channel.'}
      </p>

      <ul className="m-0 mt-3 flex flex-1 list-none flex-col justify-evenly gap-1 p-0">
        {ROLES.map((r) => {
          const m = member(r.key);
          const options = people.filter((x) => canTake(r.key, x));
          const current = m?.user_id || '';
          const draft = !!m?.user_id && isShows && !m.approved_at;
          const choices = [
            { key: '', label: 'Nobody yet' },
            ...(current && !options.some((o) => o.id === current) ? [{ key: current, label: m?.name || 'Someone' }] : []),
            ...options.map((o) => ({ key: o.id, label: o.name })),
          ];
          return (
            <li key={r.key} className="flex items-center gap-2.5 rounded-control bg-surface-container px-2.5 py-2">
              <Avatar
                name={m?.user_id ? m.name : null}
                userId={m?.user_id}
                color={m?.user_id ? m.avatar_color : null}
                shape={m?.user_id ? m.avatar_shape : null}
                accessory={m?.user_id ? m.avatar_accessory : null}
                tone={m?.user_id && !draft ? 'solid' : 'draft'}
                size="md"
                decorative
                ring="ring-surface-container"
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs leading-4 text-on-surface-variant">{r.label}{draft ? ', draft' : ''}</div>
                {canPick ? (
                  <Select
                    size="sm"
                    label={`${r.label} for this project`}
                    options={choices}
                    value={current}
                    disabled={busy}
                    onChange={(k) => run(() => setTeamMember(p.id, r.key, k || null), `Could not set the ${r.label.toLowerCase()}. Try again.`)}
                    className="mt-0.5"
                  />
                ) : (
                  <div className={`truncate text-sm font-medium ${m?.user_id ? 'text-on-surface' : 'text-on-surface-variant'}`}>{m?.name || 'Nobody yet'}</div>
                )}
              </div>
              <Appear show={canDraw && !approved} className="inline-flex flex-none">
                <Tooltip label={`Draw a different ${r.label.toLowerCase()}`}>
                  <Button variant="quiet" size="sm" disabled={busy} onClick={() => draw(r.key)} aria-label={`Redraw the ${r.label.toLowerCase()}`}>
                    Redraw
                  </Button>
                </Tooltip>
              </Appear>
            </li>
          );
        })}
      </ul>

      <Reveal show={ties.length > 0}>
        <p className="m-0 mt-2 text-xs text-on-surface-variant">
          A tie between the {ties.join(' and ')} pool was broken at random.
        </p>
      </Reveal>

    </Panel>
  );
};

export default SparkTeamPanel;
